import React, { useState, useEffect } from 'react';
import api from '../api';
import { decodeEntities } from '../utils/decodeEntities';

export default function TaskDetailModal({ task, onClose, onOpenNotes, onEdit }) {
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    loadFiles();
    loadNotes();
  }, [task.id]);

  const loadFiles = async () => {
    try {
      const ids = JSON.parse(task.meta?.gpf_file_ids || '[]');
      if (!ids.length) { setFiles([]); return; }
      const results = await Promise.all(ids.map(id => api.get(`/wp/v2/media/${id}`).catch(() => null)));
      setFiles(results.filter(Boolean).map(r => r.data));
    } catch { setFiles([]); }
  };

  const loadNotes = async () => {
    try {
      const res = await api.get(`/wp/v2/notes?task_id=${task.id}&per_page=3&orderby=date&order=desc`);
      setNotes(res.data || []);
    } catch { setNotes([]); }
  };

  const status   = task.meta?.gpf_status   || 'not_started';
  const priority = task.meta?.gpf_priority || 'medium';
  const progress = parseInt(task.meta?.gpf_progress || 0);
  const startDate = task.meta?.gpf_start_date || '';
  const endDate   = task.meta?.gpf_end_date   || '';
  const desc      = decodeEntities(task.content?.rendered)    || '';
  const today     = new Date().toISOString().split('T')[0];
  const isOverdue = endDate && endDate < today && status !== 'completed';

  const statusMap = {
    not_started: { bg: '#f8fafc', text: '#64748b', label: 'Not Started' },
    in_progress: { bg: '#eff6ff', text: '#2563eb', label: 'In Progress' },
    completed:   { bg: '#f0fdf4', text: '#16a34a', label: 'Completed' },
    overdue:     { bg: '#fef2f2', text: '#dc2626', label: 'Overdue' },
  };
  const priorityMap = {
    high:   { bg: '#fef2f2', text: '#dc2626' },
    medium: { bg: '#fffbeb', text: '#d97706' },
    low:    { bg: '#f0fdf4', text: '#16a34a' },
  };

  const sc = isOverdue ? statusMap.overdue : (statusMap[status] || statusMap.not_started);
  const pc = priorityMap[priority] || priorityMap.medium;

  const fileIcon = (mime = '') => {
    if (mime.startsWith('image/')) return '🖼';
    if (mime.includes('pdf'))      return '📄';
    if (mime.includes('word'))     return '📝';
    return '📎';
  };

  const progressColor = progress === 100 ? '#16a34a' : isOverdue ? '#dc2626' : '#2563eb';

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <div style={{ flex: 1 }}>
            <div style={styles.badgeRow}>
              <span style={{ ...styles.badge, background: sc.bg, color: sc.text }}>{sc.label}</span>
              <span style={{ ...styles.badge, background: pc.bg, color: pc.text }}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)} priority
              </span>
            </div>
            <h2 style={styles.title}>{decodeEntities(task.title?.rendered) || 'Untitled Task'}</h2>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.body}>

          {/* Description */}
          {desc
            ? <p style={styles.desc} dangerouslySetInnerHTML={{ __html: desc }} />
            : <p style={styles.descEmpty}>No description provided.</p>
          }

          {/* Stats */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Start Date</div>
              <div style={styles.statValue}>{startDate || '—'}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Due Date</div>
              <div style={{ ...styles.statValue, color: isOverdue ? '#dc2626' : '#0f172a' }}>
                {endDate || '—'}
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Progress</div>
              <div style={{ ...styles.statValue, color: progressColor }}>{progress}%</div>
            </div>
          </div>

          {/* Progress bar */}
          <div style={styles.progressWrap}>
            <div style={styles.progressBg}>
              <div style={{ ...styles.progressFill, width: `${progress}%`, background: progressColor }} />
            </div>
            <span style={{ fontSize: 12, color: '#64748b', minWidth: 32 }}>{progress}%</span>
          </div>

          {/* Assigned users */}
          <div style={styles.section}>
            <p style={styles.sectionLabel}>Assigned To</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(() => {
                try {
                  const ids = JSON.parse(task.meta?.gpf_assigned_users || '[]');
                  if (!ids.length) return <span style={{ fontSize: 13, color: '#94a3b8' }}>No one assigned</span>;
                  return (
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', background: '#f1f5f9', padding: '5px 12px', borderRadius: 99 }}>
                      👤 {ids.length} {ids.length === 1 ? 'person' : 'people'} assigned
                    </span>
                  );
                } catch { return null; }
              })()}
            </div>
          </div>

          {/* Required form / license — populated by Quick Start web search */}
          {task.meta?.gpf_form_name && (
            <div style={styles.section}>
              <p style={styles.sectionLabel}>Required Form</p>
              <div style={styles.formRow}>
                <span style={{ fontSize: 18 }}>📄</span>
                <span style={{ fontSize: 13, color: '#1e293b', flex: 1 }}>{task.meta.gpf_form_name}</span>
                {task.meta?.gpf_form_url && (
                  <a href={task.meta.gpf_form_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none', flexShrink: 0 }}>
                    Open portal ↗
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Files */}
          {files.length > 0 && (
            <div style={styles.section}>
              <p style={styles.sectionLabel}>Files ({files.length})</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {files.map(f => (
                  <a key={f.id} href={f.source_url} target="_blank" rel="noreferrer" style={styles.fileRow}>
                    <span style={{ fontSize: 16 }}>{fileIcon(f.mime_type)}</span>
                    <span style={{ fontSize: 13, color: '#1e293b', flex: 1 }}>{decodeEntities(f.title?.rendered) || 'File'}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>↗</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Recent notes */}
          {notes.length > 0 && (
            <div style={styles.section}>
              <p style={styles.sectionLabel}>Recent Notes</p>
              {notes.map(note => (
                <div key={note.id} style={styles.noteItem}>
                  <p style={styles.noteText} dangerouslySetInnerHTML={{ __html: decodeEntities(note.content?.rendered) || '' }} />
                  <span style={styles.noteMeta}>
                    {note.meta?.gpf_created_by_name || 'Team'} · {note.date ? new Date(note.date).toLocaleDateString() : ''}
                    {note.meta?.gpf_hours_logged > 0 && ` · ⏱ ${note.meta.gpf_hours_logged}h`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {notes.length === 0 && files.length === 0 && (
            <p style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '16px 0' }}>
              No notes or files yet.
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          {task.meta?.gpf_project_id && (
            <button
              style={{ ...styles.editBtn, color: '#7c3aed' }}
              onClick={() => { window.location.href = `/reports?tab=activity&project_id=${task.meta.gpf_project_id}`; }}
            >
              🕐 View Activity
            </button>
          )}
          {onEdit && (
            <button style={styles.editBtn} onClick={() => { onClose(); onEdit(task); }}>
              Edit Task
            </button>
          )}
          <button style={styles.notesBtn} onClick={() => { onClose(); onOpenNotes(task); }}>
            Open Notes
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal:       { background: 'white', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' },
  header:      { padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  badgeRow:    { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  badge:       { fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99 },
  title:       { fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.3 },
  closeBtn:    { background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', flexShrink: 0, padding: 4 },
  body:        { padding: '20px 24px' },
  desc:        { fontSize: 14, color: '#475569', lineHeight: 1.7, margin: '0 0 20px' },
  descEmpty:   { fontSize: 14, color: '#94a3b8', fontStyle: 'italic', margin: '0 0 20px' },
  statsGrid:   { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 },
  statCard:    { background: '#f8fafc', borderRadius: 10, padding: '12px 14px' },
  statLabel:   { fontSize: 11, color: '#94a3b8', marginBottom: 4 },
  statValue:   { fontSize: 15, fontWeight: 700, color: '#0f172a' },
  progressWrap:{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 },
  progressBg:  { flex: 1, height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' },
  progressFill:{ height: '100%', borderRadius: 99, transition: 'width 0.3s' },
  section:     { marginBottom: 20 },
  sectionLabel:{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 },
  avatar:      { width: 28, height: 28, borderRadius: '50%', background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 },
  fileRow:     { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, textDecoration: 'none', border: '1px solid #f1f5f9' },
  formRow:     { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' },
  noteItem:    { borderLeft: '2px solid #2563eb', paddingLeft: 12, marginBottom: 12 },
  noteText:    { fontSize: 13, color: '#1e293b', margin: '0 0 4px', lineHeight: 1.6 },
  noteMeta:    { fontSize: 11, color: '#94a3b8' },
  footer:      { padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 },
  editBtn:     { padding: '9px 20px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  notesBtn:    { padding: '9px 20px', border: 'none', borderRadius: 8, background: '#2563eb', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};